// Клиент «Настройки → Раннеры» (`/api/v1/settings/runners*`, ADR-0073).
//
// Четыре операции: список раннеров организации с выведенным статусом,
// регистрация self-hosted раннера (создаёт раннер, очередь и org-wide
// привязку атомарно), перевыпуск токена раннера и снятие раннера с
// учёта (необратимо, отказ `409` несёт число блокирующих задач в данных). Токен
// возвращается только телом `registerRunner`/`reissueRunnerToken` — он
// не попадает ни в кэш TanStack Query (список раннеров его не содержит),
// ни в localStorage; дальше токена передаётся вызывающему компоненту,
// который показывает его один раз и не хранит.
//
// Отказы — `ApiError` (`code` + данные случая), коды — в
// `docs/spec/modules/runner.md`: `retireRunner` — `409`
// `runner_has_blocking_tasks` (число — `data.blocking_task_count`), `404`
// `runner_not_found`; `reissueRunnerToken` — `404` `runner_not_found` /
// `runner_has_no_queue`.
//
// Идентификация запросов — через `authedFetch` (HttpOnly session-cookie).
// Типы вшиты руками параллельно с backend Pydantic-схемами
// (`backend/modules/runner/schemas.py`) — тот же паттерн, что в
// `features/sessions/api.ts`.

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";

export type RunnerKind = "claude_code";

export type RunnerHosting = "saas" | "self_hosted";

export type RunnerLiveness = "never_contacted" | "online" | "offline" | "stale";

export type RunnerDisplayStatus =
  | "busy"
  | "idle"
  | "offline"
  | "stale"
  | "never_contacted";

export interface RunnerBindingSummary {
  readonly is_org_wide: boolean;
  readonly project_count: number;
  readonly repo_count: number;
}

export interface RunnerDisplayResponse {
  readonly runner_id: string;
  readonly name: string | null;
  readonly kind: RunnerKind;
  readonly hosting: RunnerHosting;
  readonly liveness: RunnerLiveness;
  readonly display_status: RunnerDisplayStatus;
  readonly has_active_task: boolean;
  readonly binding: RunnerBindingSummary;
}

// Ответ успешной регистрации раннера: read-модель + токен один раз.
export interface RunnerRegistrationResponse {
  readonly runner: RunnerDisplayResponse;
  readonly runner_token: string;
}

// Ответ перевыпуска токена раннера: новый токен один раз.
export interface RunnerTokenResponse {
  readonly runner_id: string;
  readonly runner_token: string;
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Список раннеров организации с выведенным статусом (liveness из
// heartbeat, занятость, сводка scope-привязок).
export async function fetchRunners(): Promise<RunnerDisplayResponse[]> {
  const response = await authedFetch("/api/v1/settings/runners", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "fetchRunners");
  }
  return (await response.json()) as RunnerDisplayResponse[];
}

// Регистрация self-hosted раннера организации (admin). Токен в ответе
// показывается только здесь — вызывающий отображает его один раз и не
// сохраняет.
export async function registerRunner(
  name: string,
): Promise<RunnerRegistrationResponse> {
  const response = await authedFetch("/api/v1/settings/runners", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "registerRunner");
  }
  return (await response.json()) as RunnerRegistrationResponse;
}

// Перевыпуск токена раннера (admin): старый токен перестаёт действовать
// сразу. Новый токен — только в этом ответе.
export async function reissueRunnerToken(
  runnerId: string,
): Promise<RunnerTokenResponse> {
  const response = await authedFetch(
    `/api/v1/settings/runners/${runnerId}/token`,
    {
      method: "POST",
      headers: buildHeaders(),
    },
  );
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "reissueRunnerToken");
  }
  return (await response.json()) as RunnerTokenResponse;
}

// Снятие раннера с учёта (admin): необратимо, токен перестаёт действовать
// сразу. `204` — тела нет.
export async function retireRunner(runnerId: string): Promise<void> {
  const response = await authedFetch(`/api/v1/settings/runners/${runnerId}`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "retireRunner");
  }
}
